import { Injectable } from '@angular/core';
import { Ability, subject } from '@casl/ability';
import { firstValueFrom, fromEvent, Observable, takeUntil, timeout } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { AppHttpService } from './app-http.service';
import { ProjectMapSourceVersion } from './project-map-source.service';
import { type ProjectMapAsset, type ProjectMapManifest, ProjectMapManifestService, type ProjectMapStyle, type ProjectMapSymbol } from './project-map-manifest.service';

export interface MapReviewLayer { key: string; name: string; selected: boolean; }
/**
 * One source property and the colour each of its distinct values publishes with.
 *
 * `label` overrides what the legend calls a value, so colouring by a machine
 * field such as `sourceColor` can read "Existing Contours" rather than a hex
 * string. `fillColor` fills a polygon differently from its outline, which is
 * how an imported hatch keeps the fill its file authored while the outline
 * keeps the pen colour.
 */
export interface MapReviewColorBy {
  field: string;
  values: { value: string | number; color: string; label?: string; fillColor?: string }[];
}
export interface MapReviewSettings {
  color: string; strokeWidth: number; pointSize: number; fillAlpha: number;
  popupFields: string; defaultVisible: boolean; defaultOpacity: number;
  colorBy: MapReviewColorBy | null;
}
type MapGeometryFamily = ProjectMapSymbol['geometry'];
/** The style contract caps every rendition at 500 unique-value symbols. */
export const MAX_UNIQUE_VALUE_SYMBOLS = 500;
export interface MapRenditionSelection {
  sublayerKey: string; assetId: string; sha256: string; style: ProjectMapStyle;
  // The server picks the zoom range from the layer's own bounds when it is
  // omitted, which is what the review drawer does; scripts may still pin one.
  minZoom?: number; maxZoom?: number; tileSize: 256;
}
export interface MapPreparedRendition extends ProjectMapAsset {
  sourceVersionId: string; sublayerKey: string; style: ProjectMapStyle;
  bounds: [number, number, number, number]; offlineAllowed: boolean;
}
export interface MapRenditionJob {
  id: string; status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  attempts?: number; failureCode?: string; progress?: { phase: string; renditions: string[] };
}
export interface MapPublicationEntry {
  id: string; sourceVersionId: string; sublayerKey: string; renditionIds: string[]; name: string;
  groupId: string | null; order: number; defaultVisible: boolean; defaultOpacity: number;
  labelDefault: boolean; minZoom: number; maxZoom: number;
}

@Injectable({ providedIn: 'root' })
export class ProjectMapPublishingService {
  constructor(private http: AppHttpService, private manifestApi: ProjectMapManifestService, private ability: Ability) {}

  canConfigure(projectId: string): boolean {
    return !!projectId && this.ability.can('configure', subject('ProjectMapLayer', { project_id: projectId }));
  }

  canPublish(projectId: string): boolean {
    return !!projectId && this.ability.can('publish', subject('ProjectMapLayer', { project_id: projectId }));
  }

  /** Callers without a cancellation scope (a confirmed one-off action such as withdraw) may omit the signal. */
  private request<T>(request: Observable<T>, signal?: AbortSignal): Promise<T> {
    if (!signal) return firstValueFrom(request.pipe(timeout(30000)));
    signal.throwIfAborted();
    return firstValueFrom(request.pipe(timeout(30000), takeUntil(fromEvent(signal, 'abort'))));
  }

  async catalog(projectId: string, signal: AbortSignal): Promise<ProjectMapManifest | null> {
    try { return (await this.manifestApi.manifest(projectId, undefined, signal)).manifest; }
    catch (error) { if (error?.status === 404) return null; throw error; }
  }

  private canonical(value: unknown): string {
    if (Array.isArray(value)) return `[${value.map(item => this.canonical(item)).join(',')}]`;
    if (value && typeof value === 'object') {
      return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${this.canonical(value[key])}`).join(',')}}`;
    }
    return JSON.stringify(value);
  }

  requestKey(versionId: string, selections: MapRenditionSelection[], retry = 0): string {
    return `web-review-${CryptoJS.SHA256(this.canonical({ versionId, selections, retry })).toString()}`;
  }

  legacyDefaults(version: ProjectMapSourceVersion): {
    sortOrder: number; defaultVisible: false; defaultOpacity: number; warning: string;
  } | null {
    if (version.legacyReferenceLayerId === undefined && version.legacyDisplay === undefined) return null;
    const display = version.legacyDisplay;
    if (typeof version.legacyReferenceLayerId !== 'string' || !/^[A-Za-z0-9:_-]{1,128}$/.test(version.legacyReferenceLayerId)
      || !display || !Number.isInteger(display.sortOrder) || display.sortOrder < 0 || display.sortOrder > 2147483647
      || !Number.isFinite(display.defaultOpacity) || display.defaultOpacity < 0 || display.defaultOpacity > 1) {
      throw new Error('The saved layer defaults are invalid. Refresh this import before reviewing it.');
    }
    return { sortOrder: display.sortOrder, defaultVisible: false, defaultOpacity: display.defaultOpacity,
      warning: 'Original colors and symbols are not carried over. Choose a map style and review the prepared preview before publishing.' };
  }

  selections(version: ProjectMapSourceVersion, rows: MapReviewLayer[], settings: MapReviewSettings): MapRenditionSelection[] {
    const selected = rows.filter(row => row.selected);
    if (!selected.length || selected.length > 25) throw new Error('Select between 1 and 25 source layers.');
    if (!/^#[a-fA-F0-9]{6}$/.test(settings.color)) throw new Error('Choose a valid layer color.');
    const bounded = (value: number, min: number, max: number) => Number.isFinite(value) && value >= min && value <= max;
    if (!bounded(settings.strokeWidth, 0, 64) || !bounded(settings.pointSize, 0, 128)
      || !bounded(settings.fillAlpha, 0, 1) || !bounded(settings.defaultOpacity, 0, 1)) {
      throw new Error('Check the symbol sizes and opacity values.');
    }
    const fields = [...new Set(settings.popupFields.split(',').map(field => field.trim()).filter(Boolean))];
    if (fields.length > 100 || fields.some(field => field.length > 256)) throw new Error('Choose at most 100 popup fields with names up to 256 characters.');
    const symbols: ProjectMapStyle['symbols'] = [
      { geometry: 'point', color: settings.color, size: settings.pointSize },
      { geometry: 'line', color: settings.color, strokeColor: settings.color, strokeWidth: settings.strokeWidth },
      { geometry: 'polygon', color: settings.color, fillAlpha: settings.fillAlpha, strokeColor: settings.color, strokeWidth: settings.strokeWidth },
    ];
    const style: ProjectMapStyle = { symbols, popupFields: fields.map(field => ({ field, label: field })),
      legend: symbols.map(symbol => ({ label: { point: 'Points', line: 'Lines', polygon: 'Areas' }[symbol.geometry], symbol })) };
    const colorBy = this.validatedColorBy(settings.colorBy);
    return selected.map(row => {
      if (!row.name.trim() || row.name.length > 256) throw new Error('Give each selected layer a name of up to 256 characters.');
      const assets = version.assets.filter(asset => asset.sublayerKey === row.key && ['geojson', 'raster'].includes(asset.kind) && asset.role === 'geometry');
      const sublayer = version.sublayers.find(layer => layer.key === row.key);
      if (assets.length !== 1 || !sublayer) {
        throw new Error(`The normalized source for ${row.name} is unavailable. Refresh this import.`);
      }
      // Images publish view-only, so a field colour never applies to them.
      const layerStyle: ProjectMapStyle = assets[0].kind === 'raster'
        ? { symbols: [{ geometry: 'polygon', color: '#ffffff' }], legend: [], popupFields: [] }
        : colorBy ? this.coloredByField(style, settings, colorBy, this.geometryFamilies(sublayer.geometryTypes), row.name) : style;
      return { sublayerKey: row.key, assetId: assets[0].id, sha256: assets[0].sha256, style: layerStyle, tileSize: 256 };
    });
  }

  /** The symbol families a sublayer needs, from the converter's OGR geometry names. */
  geometryFamilies(geometryTypes: string[]): MapGeometryFamily[] {
    const families = new Set<MapGeometryFamily>();
    for (const name of geometryTypes || []) {
      const type = String(name).toUpperCase();
      if (type.includes('POINT')) families.add('point');
      else if (type.includes('LINESTRING') || type.includes('LINEARRING') || type.includes('CURVE')) families.add('line');
      else if (type.includes('POLYGON') || type.includes('SURFACE')) families.add('polygon');
      // A geometry collection or an unrecognized name may hold anything, so
      // cover every family rather than leave a feature without a symbol.
      else return ['point', 'line', 'polygon'];
    }
    const ordered: MapGeometryFamily[] = ['point', 'line', 'polygon'];
    const present = ordered.filter(family => families.has(family));
    return present.length ? present : ordered;
  }

  private validatedColorBy(colorBy: MapReviewColorBy | null | undefined): MapReviewColorBy | null {
    if (!colorBy) return null;
    const field = colorBy.field?.trim() || '';
    if (!field || field.length > 256 || !colorBy.values?.length) throw new Error('Choose a field and at least one value to color by.');
    const seen = new Set<string>();
    for (const entry of colorBy.values) {
      const numeric = typeof entry.value === 'number' && Number.isFinite(entry.value);
      if (!numeric && (typeof entry.value !== 'string' || !entry.value.length || entry.value.length > 256)) {
        throw new Error('Choose a field whose values are text or numbers to color by.');
      }
      if (!/^#[a-fA-F0-9]{6}$/.test(entry.color)) throw new Error('Choose a valid color for each value.');
      if (entry.fillColor !== undefined && !/^#[a-fA-F0-9]{6}$/.test(entry.fillColor)) throw new Error('Choose a valid color for each value.');
      if (entry.label !== undefined && (typeof entry.label !== 'string' || entry.label.length > 256)) {
        throw new Error('Give each colored value a legend label of up to 256 characters.');
      }
      // `render.py` matches on the first entry for a value, so duplicates would
      // silently drop a colour.  JSON keeps numbers and strings distinct.
      const key = `${typeof entry.value}:${entry.value}`;
      if (seen.has(key)) throw new Error('Each value can have only one color.');
      seen.add(key);
    }
    return { field, values: colorBy.values.map(entry => ({ value: entry.value, color: entry.color,
      ...(entry.fillColor ? { fillColor: entry.fillColor } : {}),
      ...(entry.label?.trim() ? { label: entry.label.trim() } : {}) })) };
  }

  private coloredByField(style: ProjectMapStyle, settings: MapReviewSettings, colorBy: MapReviewColorBy,
    families: MapGeometryFamily[], layerName: string): ProjectMapStyle {
    if (colorBy.values.length * families.length > MAX_UNIQUE_VALUE_SYMBOLS) {
      throw new Error(`Color ${layerName} by a field with fewer values; a prepared layer supports at most ${MAX_UNIQUE_VALUE_SYMBOLS} unique-value symbols.`);
    }
    // A polygon carries one fill plus a stroke, so a value that also names a
    // fill colour fills with it and outlines in its own colour; points and
    // lines have a single colour and ignore it.
    const symbolFor = (family: MapGeometryFamily, color: string, fillColor?: string): ProjectMapSymbol => family === 'point'
      ? { geometry: 'point', color, size: settings.pointSize }
      : family === 'line' ? { geometry: 'line', color, strokeColor: color, strokeWidth: settings.strokeWidth }
        : { geometry: 'polygon', color: fillColor || color, fillAlpha: settings.fillAlpha, strokeColor: color, strokeWidth: settings.strokeWidth };
    const values = colorBy.values.flatMap(entry => families.map(family => ({ value: entry.value, symbol: symbolFor(family, entry.color, entry.fillColor) })));
    return { ...style, uniqueValues: { field: colorBy.field, values },
      legend: colorBy.values.map(entry => ({ label: entry.label?.trim() || String(entry.value),
        symbol: symbolFor(families[0], entry.color, entry.fillColor) })) };
  }

  /**
   * Recover the colour-by selection from a published style, so reopening a
   * layer keeps its field colouring instead of flattening it to one colour.
   * A value carries one symbol per geometry family; its first symbol holds the
   * colour the publisher chose. Anything the form could not publish itself
   * gives up the whole selection rather than a half-recovered one.
   *
   * A polygon symbol whose fill differs from its stroke came from a file fill,
   * so the stroke is the value's colour and the fill is read back separately.
   * Legend labels are written one per value in the same order, so a legend of
   * matching length gives each value the name the publisher stored.
   */
  publishedColorBy(style: ProjectMapStyle | undefined | null): MapReviewColorBy | null {
    const unique = style?.uniqueValues;
    if (!unique?.field || !unique.values?.length) return null;
    const hex = (value: unknown): value is string => typeof value === 'string' && /^#[a-fA-F0-9]{6}$/.test(value);
    const values: MapReviewColorBy['values'] = [];
    const seen = new Set<string>();
    for (const entry of unique.values) {
      const numeric = typeof entry.value === 'number' && Number.isFinite(entry.value);
      if ((!numeric && typeof entry.value !== 'string') || !hex(entry.symbol?.color)) return null;
      const key = `${typeof entry.value}:${entry.value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const filled = entry.symbol.geometry === 'polygon' && hex(entry.symbol.strokeColor)
        && entry.symbol.strokeColor !== entry.symbol.color;
      values.push(filled
        ? { value: entry.value, color: entry.symbol.strokeColor, fillColor: entry.symbol.color }
        : { value: entry.value, color: entry.symbol.color });
    }
    const legend = style?.legend || [];
    if (legend.length === values.length) {
      values.forEach((entry, index) => {
        const label = legend[index]?.label;
        if (typeof label === 'string' && label.trim() && label !== String(entry.value)) entry.label = label.trim().slice(0, 256);
      });
    }
    return values.length ? { field: unique.field, values } : null;
  }

  /**
   * Whether a failure while drawing one imported sheet must abort the whole
   * location preview, rather than leaving that sublayer's footprint outline on
   * its own. Only a cancelled panel and a lost session do: a missing object, an
   * expired grant, a hash mismatch or a network error all degrade to the
   * footprint, because the outline still answers "where did this land?".
   */
  previewFailureIsFatal(error: { name?: string; status?: number } | null | undefined): boolean {
    return error?.name === 'AbortError' || [401, 403].includes(error?.status);
  }

  previewBounds(value: unknown): [number, number, number, number] {
    if (!Array.isArray(value) || value.length !== 4 || !value.every(item => typeof item === 'number' && Number.isFinite(item))
      || value[0] > value[2] || value[1] > value[3] || value[0] < -180 || value[2] > 180
      || value[1] < -85.0511287798066 || value[3] > 85.0511287798066) {
      throw new Error('Location bounds are invalid. Confirm the source coordinate system before reviewing this file.');
    }
    return value as [number, number, number, number];
  }

  estimate(version: ProjectMapSourceVersion, selections: MapRenditionSelection[]): { tiles: number; bytes: number } {
    let tiles = 0;
    let bytes = 0;
    for (const selection of selections) {
      const layer = version.sublayers.find(item => item.key === selection.sublayerKey);
      const asset = version.assets.find(item => item.id === selection.assetId && item.sublayerKey === selection.sublayerKey);
      const bounds = layer?.bounds;
      if (!bounds || bounds.length !== 4 || !bounds.every(Number.isFinite) || bounds[0] > bounds[2]
        || bounds[1] > bounds[3] || bounds[0] < -180 || bounds[2] > 180
        || bounds[1] < -85.0511287798066 || bounds[3] > 85.0511287798066) {
        throw new Error(`Location bounds for ${layer?.name || 'this layer'} need review before preparation.`);
      }
      const buffer = Math.max(...selection.style.symbols.map(symbol => (symbol.size ?? 0) / 2 + (symbol.strokeWidth ?? 0)));
      // A selection that leaves the zoom range to the server cannot be counted
      // here; the server sizes its own range. The bounds check above still runs.
      if (!Number.isInteger(selection.minZoom) || !Number.isInteger(selection.maxZoom)) continue;
      let count = 0;
      for (let z = selection.minZoom; z <= selection.maxZoom; z++) {
        const size = 2 ** z;
        const x = (lon: number) => (lon + 180) / 360 * size * 256;
        const y = (lat: number) => (1 - Math.asinh(Math.tan(lat * Math.PI / 180)) / Math.PI) / 2 * size * 256;
        const left = Math.max(0, Math.floor((x(bounds[0]) - buffer) / 256));
        const right = Math.min(size - 1, Math.floor((x(bounds[2]) + buffer) / 256));
        const top = Math.max(0, Math.floor((y(bounds[3]) - buffer) / 256));
        const bottom = Math.min(size - 1, Math.floor((y(bounds[1]) + buffer) / 256));
        count += (right - left + 1) * (bottom - top + 1);
      }
      const estimatedBytes = count * 256 * 256 * 4 + 3 * asset.bytes + 2 * 1024 * 1024;
      if (count > 2048 || estimatedBytes > 128 * 1024 * 1024) throw new Error(`Reduce the maximum zoom for ${layer.name}; its prepared tiles exceed the size limit.`);
      tiles += count; bytes += estimatedBytes;
    }
    if (tiles + selections.length * 3 > 4098 || bytes > 128 * 1024 * 1024) {
      throw new Error('Prepare fewer layers together or reduce the maximum zoom to stay within the size limit.');
    }
    return { tiles, bytes };
  }

  prepare(projectId: string, versionId: string, selections: MapRenditionSelection[], key: string, signal: AbortSignal): Promise<{ job: MapRenditionJob }> {
    if (!this.canConfigure(projectId)) return Promise.reject(Object.assign(new Error('You cannot prepare project layers.'), { status: 403 }));
    return this.request(this.http.postService(`/v1/project/${projectId}/map-source-versions/${versionId}/renditions`, { selections }, {},
      { headers: { 'Idempotency-Key': key } }), signal);
  }

  job(projectId: string, jobId: string, signal: AbortSignal): Promise<MapRenditionJob> {
    return this.request(this.http.getService(`/v1/project/${projectId}/map-rendition-jobs/${jobId}`), signal);
  }

  /** Every prepared rendition of a version, oldest first; the last row carries the newest draft style. */
  async preparedRows(projectId: string, versionId: string, signal: AbortSignal): Promise<MapPreparedRendition[]> {
    return this.request<MapPreparedRendition[]>(this.http.getService(`/v1/project/${projectId}/map-source-versions/${versionId}/renditions`), signal);
  }

  async prepared(projectId: string, versionId: string, selections: MapRenditionSelection[], job: MapRenditionJob, signal: AbortSignal): Promise<MapPreparedRendition[]> {
    if (job.status !== 'succeeded' || !job.progress?.renditions?.length) throw new Error('Preparation has not completed.');
    const rows = await this.request<MapPreparedRendition[]>(this.http.getService(`/v1/project/${projectId}/map-source-versions/${versionId}/renditions`), signal);
    const ids = new Set(job.progress.renditions);
    const selected = rows.filter(row => ids.has(row.id));
    if (selected.length !== ids.size || selected.some(row => {
      const selection = selections.find(item => item.sublayerKey === row.sublayerKey);
      return !selection || row.sourceVersionId !== versionId || this.canonical(row.style) !== this.canonical(selection.style)
        || (row.kind === 'raster_xyz' && ((selection.minZoom !== undefined && row.minZoom !== selection.minZoom)
          || (selection.maxZoom !== undefined && row.maxZoom !== selection.maxZoom) || row.tileSize !== selection.tileSize));
    })) throw new Error('Prepared output does not match the selected source and style. Prepare it again.');
    if (selections.some(selection => !selected.some(row => row.sublayerKey === selection.sublayerKey && row.kind === 'raster_xyz' && row.role === 'geometry'))) {
      throw new Error('This preparation has no raster output to review.');
    }
    return selected;
  }

  previewGrant(projectId: string, renditionId: string, signal: AbortSignal): Promise<{ grant: string; expiresIn: number }> {
    return this.request(this.http.postService(`/v1/project/${projectId}/map-renditions/${renditionId}/preview-grant`, {}), signal);
  }

  sourcePreviewGrants(projectId: string, versionId: string, assetIds: string[], signal: AbortSignal): Promise<{
    grants: { assetId: string; url: string; expiresIn: number }[];
  }> {
    return this.request(this.http.postService(`/v1/project/${projectId}/map-source-versions/${versionId}/preview-grants`, { assetIds }), signal);
  }

  async readPreview(url: string, signal: AbortSignal, expected?: { bytes: number; sha256: string }): Promise<Uint8Array | null> {
    const controller = new AbortController();
    signal.throwIfAborted();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    const deadline = setTimeout(abort, 20000);
    let reader: ReadableStreamDefaultReader<Uint8Array>;
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'omit', redirect: 'error',
        referrerPolicy: 'no-referrer', cache: 'no-store' });
      if (!response.ok) throw Object.assign(new Error('Private map preview is unavailable. Refresh the preview to try again.'), { status: response.status });
      if (response.status === 204 && !expected) return null;
      const limit = expected?.bytes ?? 2 * 1024 * 1024;
      if (!limit || limit > 2 * 1024 * 1024 || Number(response.headers.get('Content-Length')) > limit || !response.body) {
        throw new Error('The preview exceeds the size limit. Prepare the map preview instead.');
      }
      reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let length = 0;
      while (true) {
        const chunk = await reader.read();
        if (chunk.done) break;
        length += chunk.value.length;
        if (length > limit) throw new Error('The preview exceeds the size limit. Prepare the map preview instead.');
        chunks.push(chunk.value);
      }
      const data = new Uint8Array(length);
      let offset = 0;
      for (const chunk of chunks) { data.set(chunk, offset); offset += chunk.length; }
      if (expected && (length !== expected.bytes || CryptoJS.SHA256(CryptoJS.lib.WordArray.create(data)).toString() !== expected.sha256)) {
        throw new Error('The source preview did not match its verified content. Refresh it to try again.');
      }
      if (!expected && (length < 8 || ![137, 80, 78, 71, 13, 10, 26, 10].every((byte, index) => data[index] === byte))) {
        throw new Error('The prepared map tile could not be verified. Refresh the preview.');
      }
      return data;
    } catch (error) {
      if (signal.aborted) throw new DOMException('Preview closed', 'AbortError');
      if (error?.name === 'AbortError') throw new Error('The map preview timed out. Refresh it to try again.');
      throw error;
    } finally {
      void reader?.cancel().catch(() => undefined);
      controller.abort(); clearTimeout(deadline); signal.removeEventListener('abort', abort);
    }
  }

  tileUrl(grant: string): string { return this.manifestApi.tileUrl(grant, '{level}', '{col}', '{row}'); }

  display(projectId: string, sourceVersionId: string, row: MapReviewLayer, signal: AbortSignal): Promise<{ id: string }> {
    return this.request(this.http.postService(`/v1/project/${projectId}/map-layers/display`, {
      sourceVersionId, sublayerKey: row.key, name: row.name.trim(),
    }), signal);
  }

  /** `group` null publishes the additions ungrouped and leaves the existing groups untouched. */
  publication(catalog: ProjectMapManifest | null, additions: MapPublicationEntry[], group: ProjectMapManifest['groups'][number] | null): {
    expectedCatalogRevision: number; entries: MapPublicationEntry[]; groups: ProjectMapManifest['groups'];
  } {
    const ids = new Set(additions.map(entry => entry.id));
    const existing = (catalog?.layers || []).filter(layer => !ids.has(layer.id)).map(layer => ({
      id: layer.id, sourceVersionId: layer.sourceVersionId, sublayerKey: layer.sublayerKey,
      renditionIds: layer.assets.map(asset => asset.id), name: layer.name, groupId: layer.groupId, order: layer.order,
      defaultVisible: layer.defaultVisible, defaultOpacity: layer.defaultOpacity, labelDefault: layer.labelDefault,
      minZoom: layer.minZoom, maxZoom: layer.maxZoom,
    }));
    const groups = [...(catalog?.groups || [])];
    if (group && !groups.some(item => item.id === group.id)) groups.push(group);
    return { expectedCatalogRevision: catalog?.catalogRevision || 0, entries: [...existing, ...additions], groups };
  }

  /** Take one published layer off the map; the next revision omits it and the row is withdrawn. */
  withdraw(projectId: string, layerId: string, expectedCatalogRevision: number, signal?: AbortSignal): Promise<{ catalogRevision: number }> {
    if (!this.canPublish(projectId)) return Promise.reject(Object.assign(new Error('You cannot change project layers.'), { status: 403 }));
    return this.request(this.http.postService(`/v1/project/${projectId}/map-layers/${encodeURIComponent(layerId)}/withdraw`, { expectedCatalogRevision }), signal);
  }

  publish(projectId: string, publication: ReturnType<ProjectMapPublishingService['publication']>, signal: AbortSignal): Promise<{ catalogRevision: number }> {
    if (!this.canPublish(projectId)) return Promise.reject(Object.assign(new Error('You cannot publish project layers.'), { status: 403 }));
    return this.request(this.http.postService(`/v1/project/${projectId}/map-layers/publish`, publication), signal);
  }
}
